import { CSSProperties, RefObject, useLayoutEffect, useState } from 'react';

/**
 * Viewport-fixed placement for a popover opened from a trigger: just under the host element, shifted left as needed so
 * it never runs off the screen. Being `fixed` it also escapes ancestors that clip their overflow (chart cards, scroll
 * areas). Re-places itself on resize and on scroll of any ancestor.
 */
export function useFixedPopover(open: boolean, hostRef: RefObject<HTMLElement | null>, width: number): CSSProperties {
  const [style, setStyle] = useState<CSSProperties>({ position: 'fixed', visibility: 'hidden', width });

  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const host = hostRef.current;
      if (!host) return;
      const rect = host.getBoundingClientRect();
      const fitted = Math.min(width, window.innerWidth - 16);
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - fitted - 8));
      const top = rect.bottom + 8;
      // Scrolling anywhere on the page calls this: only re-render when the popover actually has to move.
      setStyle((prev) => (prev.top === top && prev.left === left && prev.width === fitted && prev.visibility !== 'hidden'
        ? prev
        : { position: 'fixed', top, left, width: fitted }));
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, hostRef, width]);

  return style;
}
